import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  AppBar,
  Toolbar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Phone,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  SupportAgent,
  CreditCard,
  Lock,
  WhatsApp,
  ExpandMore,
  HelpOutline
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import landingHealthTips from '../data/landingHealthTips';
import faqs from '../data/faqs';

// Central contact details for the helpdesk
const SUPPORT_PHONE_DISPLAY = '+234 704 535 0299';
const SUPPORT_PHONE_TEL = '+2347045350299';
const SUPPORT_WHATSAPP = '2347045035024';

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tipSlideIndex, setTipSlideIndex] = useState(0);

  const tipsPerSlide = isMobile ? 1 : 2;
  const tipSlideCount = Math.ceil(landingHealthTips.length / tipsPerSlide);

  useEffect(() => {
    setTipSlideIndex(0);
  }, [tipsPerSlide]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTipSlideIndex((prev) => (prev + 1) % tipSlideCount);
    }, 6000);
    return () => clearInterval(timer);
  }, [tipSlideCount]);

  const visibleTips = landingHealthTips.slice(
    tipSlideIndex * tipsPerSlide,
    tipSlideIndex * tipsPerSlide + tipsPerSlide
  );

  const goToPreviousTips = () => {
    setTipSlideIndex((prev) => (prev - 1 + tipSlideCount) % tipSlideCount);
  };

  const goToNextTips = () => {
    setTipSlideIndex((prev) => (prev + 1) % tipSlideCount);
  };
  const features = [
    {
      title: 'Pharmacies',
      description: 'Manage your inventory, receive orders, and connect with delivery riders seamlessly.',
      features: ['Product Management', 'Order Processing', 'Rider Network', 'Real-time Alerts'],
      image: '/hero-pharmacist.png',
      cta: 'Join as Pharmacy'
    },
    {
      title: 'Customers',
      description: 'Browse medicines, place orders, and track deliveries from your favorite pharmacies.',
      features: [
        'Order prescription medicines online',
        'Get it delivered to your door',
        'Fast and reliable service',
        'Health and Wellness Tips'
      ],
      image: '/customer-card.jpg',
      cta: 'Order Medicines'
    },
    {
      title: 'Riders',
      description: 'Get delivery opportunities in your area and manage your delivery schedule.',
      features: ['Location-based Orders', 'Real-time Updates', 'Earnings Tracking', 'Flexible Schedule'],
      image: '/feature-riders.png',
      cta: 'Ride with PD'
    }
  ];

  return (
    <Box>
      {/* Top contact bar */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 1, sm: 1.25 },
          px: 2
        }}
      >
        <Container maxWidth="lg" disableGutters sx={{ px: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 1, sm: 1.5 },
              textAlign: 'center',
              fontSize: { xs: '0.8rem', sm: '0.95rem' },
              fontWeight: 500
            }}
          >
            <Box
              component="a"
              href="tel:+2347045350299"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <Phone sx={{ fontSize: { xs: 16, sm: 18 } }} />
              Call +234 704 535 0299
            </Box>
            <Typography component="span" sx={{ opacity: 0.85, display: { xs: 'none', sm: 'inline' } }}>
              or
            </Typography>
            <Box
              component="a"
              href="https://wa.me/2347045035024"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                aria-hidden="true"
                sx={{ width: { xs: 16, sm: 18 }, height: { xs: 16, sm: 18 }, fill: 'currentColor' }}
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </Box>
              Chat +234 704 503 5024
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box component="img" src="/pd-logo.png" alt="Prescription Drives" loading="eager" width="36" height="36" sx={{ height: 36, mr: 1 }} />
            <Typography variant="h6" component="div" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Prescription Drives
            </Typography>
          </Box>
          <Button
            color="primary"
            onClick={() => {
              document.getElementById('help')?.scrollIntoView({ behavior: 'smooth' });
            }}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Help
          </Button>
          <Button color="primary" onClick={() => navigate('/login')}>
            Login
          </Button>
          <Button variant="contained" onClick={() => navigate('/register')} sx={{ ml: 1 }}>
            Sign Up
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: { xs: 'auto', md: 560 },
          background: 'linear-gradient(135deg, #0F3D8C 0%, #1E5BD7 45%, #4FC3F7 100%)',
          color: 'white',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            flex: { xs: 'none', md: '1 1 48%' },
            display: 'flex',
            alignItems: 'center',
            px: { xs: 3, sm: 5, md: 6, lg: 8 },
            py: { xs: 6, md: 8 },
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
          <Box sx={{ maxWidth: 520, mx: { xs: 'auto', md: 0 } }}>
            <Typography
              variant="h6"
              component="h1"
              gutterBottom
              sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' }, letterSpacing: 1.2, fontWeight: 700 }}
            >
              PRESCRIPTION DRIVES
            </Typography>
            <Typography
              component="h2"
              sx={{
                mb: 2,
                opacity: 0.95,
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                fontWeight: 500,
                color: '#B3E5FC'
              }}
            >
              Pharmacy Delivery Made Simple
            </Typography>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              component="h3"
              gutterBottom
              fontWeight="bold"
              sx={{ mb: 2, lineHeight: 1.15 }}
            >
              Wellness and pharmacy Services
            </Typography>
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{ mb: 4, opacity: 0.92, fontWeight: 400, lineHeight: 1.5 }}
            >
              Connect pharmacies, customers, and delivery riders in one seamless platform
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 3.5,
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: { xs: 'none', md: '1 1 52%' },
            position: 'relative',
            minHeight: { xs: 280, sm: 360, md: 'auto' },
            alignSelf: 'stretch'
          }}
        >
          <Box
            component="img"
            src="/hero-customer.jpg"
            alt="Customer ordering pharmacy delivery on Prescription Drives"
            loading="eager"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center center',
              display: 'block',
              minHeight: { xs: 280, sm: 360, md: 560 }
            }}
          />
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(15,61,140,0.45) 0%, rgba(15,61,140,0.05) 35%, transparent 60%)',
              pointerEvents: 'none'
            }}
          />
        </Box>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom fontWeight="bold">
        A pharmacy, Customer or Delivery Rider? We've got you covered.
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
        PD app: Wellness and pharmacy Services
        </Typography>

        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid size={{ xs: 12, md: 4 }} key={feature.title}>
              <Box
                sx={{
                  position: 'relative',
                  minHeight: { xs: 420, sm: 460 },
                  height: '100%',
                  overflow: 'hidden',
                  backgroundImage: `url(${feature.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  color: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(8,24,56,0.15) 0%, rgba(8,24,56,0.55) 45%, rgba(8,24,56,0.88) 100%)'
                  }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2.5, sm: 3 } }}>
                  <Typography
                    variant="h4"
                    component="h3"
                    fontWeight="bold"
                    sx={{ mb: 1.5, fontSize: { xs: '1.6rem', sm: '1.85rem' }, lineHeight: 1.2 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, opacity: 0.95, fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.5 }}
                  >
                    {feature.description}
                  </Typography>
                  <Box sx={{ mb: 2.5 }}>
                    {feature.features.map((item) => (
                      <Typography
                        key={item}
                        variant="body2"
                        sx={{ mb: 0.75, opacity: 0.9, fontSize: { xs: '0.85rem', sm: '0.92rem' } }}
                      >
                        • {item}
                      </Typography>
                    ))}
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/register')}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontWeight: 700,
                      px: 2.5,
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    {feature.cta}
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Health Tips Slideshow */}
      <Box
        sx={{
          bgcolor: '#2C60C9',
          background: 'linear-gradient(135deg, #2ED6AC 0%, #2C60C9 100%)',
          py: { xs: 6, md: 8 },
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' }, mb: 1.5 }}
          >
            Health Tips
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            sx={{
              mb: { xs: 4, md: 5 },
              maxWidth: 720,
              mx: 'auto',
              opacity: 0.95,
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.15rem' },
              lineHeight: 1.5
            }}
          >
            Practical wellness advice from Prescription Drives — stay healthy between pharmacy deliveries.
          </Typography>

          <Grid container spacing={3} justifyContent="center" sx={{ minHeight: { xs: 360, sm: 420 } }}>
            {visibleTips.map((tip) => (
              <Grid size={{ xs: 12, md: 6 }} key={`${tipSlideIndex}-${tip.title}`}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'white',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.12)'
                  }}
                >
                  <CardMedia
                    component="img"
                    height="220"
                    image={tip.image}
                    alt={tip.title}
                    loading="lazy"
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ px: { xs: 2.5, sm: 3 }, py: { xs: 2.5, sm: 3 }, textAlign: 'center' }}>
                    <Typography
                      variant="h5"
                      component="h3"
                      fontWeight="bold"
                      color="text.primary"
                      sx={{ mb: 1.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                    >
                      {tip.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.6 }}
                    >
                      {tip.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mt: 4
            }}
          >
            <IconButton
              onClick={goToPreviousTips}
              aria-label="Previous health tips"
              sx={{
                bgcolor: 'rgba(255,255,255,0.25)',
                color: 'white',
                width: 44,
                height: 44,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' }
              }}
            >
              <KeyboardArrowLeft fontSize="large" />
            </IconButton>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {Array.from({ length: tipSlideCount }).map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setTipSlideIndex(index)}
                  sx={{
                    width: index === tipSlideIndex ? 28 : 10,
                    height: 10,
                    borderRadius: 5,
                    bgcolor: index === tipSlideIndex ? 'white' : 'rgba(255,255,255,0.45)',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease'
                  }}
                />
              ))}
            </Box>

            <IconButton
              onClick={goToNextTips}
              aria-label="Next health tips"
              sx={{
                bgcolor: 'rgba(255,255,255,0.25)',
                color: 'white',
                width: 44,
                height: 44,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' }
              }}
            >
              <KeyboardArrowRight fontSize="large" />
            </IconButton>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: '#2C60C9',
                fontWeight: 700,
                px: 4,
                '&:hover': { bgcolor: '#E8F8F4' }
              }}
            >
              Sign up for more tips
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Security Features */}
       {/* <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom fontWeight="bold">
            Advanced Security Features
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Built with security and safety in mind
          </Typography>

          <Grid container spacing={4}>
            {securityFeatures.map((feature, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2, color: 'primary.main' }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Pricing Section */}
      {/* <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom fontWeight="bold">
          Simple Pricing
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Transparent pricing with location-based rates
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ textAlign: 'center', p: 4 }}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  Nigeria
                </Typography>
                <Typography variant="h3" color="primary.main" gutterBottom fontWeight="bold">
                  ₦250
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Per delivery within Nigeria
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ textAlign: 'center', p: 4 }}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  International
                </Typography>
                <Typography variant="h3" color="secondary.main" gutterBottom fontWeight="bold">
                  $1
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Per delivery outside Nigeria
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container> */}

      {/* Helpdesk Section */}
      <Box id="help" sx={{ bgcolor: '#F5F8FF', py: { xs: 5, md: 7 }, borderTop: '1px solid #e8eef5' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <SupportAgent sx={{ fontSize: 46, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Need Help? Talk to Us
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Customers and pharmacies can reach our helpdesk anytime. Call us or start a
              WhatsApp chat and we’ll be happy to assist.
            </Typography>
          </Box>

          <Grid container spacing={2} justifyContent="center">
            <Grid size={{ xs: 12, sm: 6, md: 5 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Phone sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Call the Helpdesk
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Speak with our support team directly.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Phone />}
                    href={`tel:${SUPPORT_PHONE_TEL}`}
                    fullWidth
                  >
                    {SUPPORT_PHONE_DISPLAY}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 5 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <WhatsApp sx={{ fontSize: 36, color: '#25D366', mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Chat on WhatsApp
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Get quick answers via chat.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<WhatsApp />}
                    href={`https://wa.me/${SUPPORT_WHATSAPP}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1da851' } }}
                  >
                    Start Chat
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" sx={{ bgcolor: 'white', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <HelpOutline sx={{ fontSize: 46, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Answers to common questions from customers and pharmacies.
            </Typography>
          </Box>

          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              disableGutters
              sx={{
                mb: 1,
                borderRadius: 2,
                '&:before': { display: 'none' },
                border: '1px solid #e8eef5'
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight="600">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="h6" textAlign="center" gutterBottom>
            Prescription Drives - Pharmacy Delivery Platform
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.8, mb: 2 }}>
            Connecting pharmacies, customers, and delivery riders for a safer, more efficient healthcare delivery system.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
            <Phone sx={{ fontSize: 18 }} />
            <Typography variant="body1" component="a" href="tel:+2347045350299" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              +234 704 535 0299
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Trust signals */}
      <Box sx={{ bgcolor: 'white', py: { xs: 4, md: 5 }, borderTop: '1px solid #e8eef5' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 6 }} justifyContent="center">
            <Grid size={{ xs: 12, sm: 6, md: 5 }}>
              <Box
                component="a"
                href="https://wa.me/2347045035024"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover .trust-title': { color: 'primary.dark' }
                }}
              >
                <Box sx={{ position: 'relative', flexShrink: 0, mt: 0.25 }}>
                  <SupportAgent sx={{ fontSize: 42, color: 'primary.main' }} />
                </Box>
                <Box>
                  <Typography
                    className="trust-title"
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: 'primary.main', mb: 0.5, fontSize: { xs: '1.05rem', sm: '1.15rem' } }}
                  >
                    Top-notch support
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Get help anytime by phone or WhatsApp chat.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ position: 'relative', flexShrink: 0, mt: 0.25 }}>
                  <CreditCard sx={{ fontSize: 42, color: 'primary.main' }} />
                  <Lock
                    sx={{
                      position: 'absolute',
                      left: -4,
                      bottom: 2,
                      fontSize: 18,
                      color: '#2ED6AC',
                      bgcolor: 'white',
                      borderRadius: '50%'
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: 'primary.main', mb: 0.5, fontSize: { xs: '1.05rem', sm: '1.15rem' } }}
                  >
                    Secure payments
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Make digital payments securely using Paystack.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Promoted By Section */}
      <Box sx={{ bgcolor: 'white', py: 3, borderTop: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Promoted by Economic and Business Strategies (EBS)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/ebs-logo.png"
                alt="Economic and Business Strategies"
                loading="lazy"
                sx={{ height: { xs: 44, sm: 52 }, width: 'auto', maxWidth: 220 }}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
